<template>
  <SidePanel :stateless="!!loading" v-if="canSeeMeetings" v-model="showMeetingDetails" :title="meetingTitle">
    <template #title>
      <div class="d-flex flex-grow-1" v-if="!showDocs && showOpenAccountForm && !waitingForMeetingProcess">
        <template v-for="(step, i) of accountOpenSteps">
          <v-chip :key="i" v-if="i === accountOpenStep" class="ma-2" color="primary" text-color="white">
            <v-avatar left class="primary darken-2">
              {{ i + 1 }}
            </v-avatar>
            {{ step.title }}
          </v-chip>
          <v-avatar :key="i" size="32" class="grey lighten-2 ma-2" v-else>
            {{ i + 1 }}
          </v-avatar>
        </template>
        <v-spacer></v-spacer>
        <v-avatar style="cursor: pointer;" class="error ma-2" @click="showMeetingCloseModal = true" size="32">
          <v-icon class="white--text">mdi-cancel</v-icon>
        </v-avatar>
      </div>
      <h2 v-else class="mt-n5 pb-6 mt-sm-0 font font--h1 pl-4 pr-12 px-sm-0 py-sm-4">
        {{ meetingTitle }}
      </h2>
    </template>

    <MeetingDetails :showDocs="showDocs" />

    <!-- Модалка закрытия встречи -->

    <v-dialog v-model="showMeetingCloseModal" persistent max-width="320">
      <v-card>
        <v-card-title class="headline">
          Закрыть встречу?
        </v-card-title>
        <v-card-text>Встреча действительно не состоялась и ее нужно закрыть?</v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="error darken-1" text @click="meetingFailed">
            Закрыть
          </v-btn>
          <v-btn color="primary" text @click="showMeetingCloseModal = false">
            Отмена
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <v-dialog v-model="showAddCommentModal" max-width="500px">
      <v-card>
        <v-card-title>
          Добавить комментарий к встрече
        </v-card-title>
        <v-card-text>
          <v-textarea v-model="commentText" label="Текст комментария" auto-grow />
        </v-card-text>
        <v-card-actions>
          <v-btn text @click="showAddCommentModal = false">
            Закрыть
          </v-btn>
          <v-spacer />
          <v-btn :disabled="!commentText" color="primary" text @click="sendComment(commentText).then(() => showAddCommentModal = false)">
            Отправить
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <template #append>
      <div v-if="showOpenAccountForm && !waitingForMeetingProcess" class="d-flex flex-wrap" style="row-gap: 0.5em">
        <v-btn v-if="accountOpenStep === 0" :disabled="showDocs" @click="showDocs = true" class="mr-3" color="secondary">
          Документы
        </v-btn>
        <v-btn v-else @click="accountOpenStepBack" class="mr-3" color="secondary">
          Назад
        </v-btn>

        <v-btn v-if="canAddMeetingComment && !showDocs" @click="showAddCommentModal = true" class="mr-3" color="blue-grey lighten-2">
          Добавить комментарий
        </v-btn>

        <v-spacer></v-spacer>

        <!-- Кнопка вперед / отправить доки -->

        <div v-show="!showDocs">
          <template v-if="!wait_sign">
            <v-btn v-if="isCurrentStepSurvey && isSurveyCanSkip && sendDocsDisabled" @click="accountOpenStepNext" color="primary">
              Пропустить
            </v-btn>
            <v-btn v-else-if="!isCurrentStepComplete" :disabled="sendDocsDisabled" @click="sendDocs" color="primary">
              Отправить документы
            </v-btn>
          </template>
          <v-banner v-else-if="!isCurrentStepComplete" icon="mdi-account-clock" icon-color="accent darken-1" single-line>
            Ожидается электронная подпись клиентом
          </v-banner>
        </div>

        <v-btn v-if="showDocs" @click="showDocs = false" color="primary">Открыть счет</v-btn>

        <v-btn
          v-else-if="isCurrentStepComplete"
          :disabled="accountOpenStep === accountOpenStepsCount - 1"
          @click="accountOpenStepNext"
          color="primary"
          >Вперед</v-btn
        >
      </div>
      <template v-else-if="!waitingForMeetingProcess">
        <v-btn @click="openAccountForm" class="mr-3" color="primary">Начать встречу</v-btn>
        <v-btn @click="showMeetingCloseModal = true" color="error">Встреча не состоялась</v-btn>
      </template>
    </template>
  </SidePanel>
</template>

<script>
import SidePanel from "@/components/common/SidePanel";
import MeetingDetails from "@/components/meeting/MeetingDetails";

import { mapState, mapMutations, mapGetters, mapActions } from "vuex";
import { processMeetingSendFls, processMeetingSendDocs, processMeetingSurvey, processMeetingTemplateFields, breakMeeting } from "@/api";

export default {
  components: {
    SidePanel,
    MeetingDetails,
  },

  data() {
    return {
      showDocs: false,
      showMeetingCloseModal: false,

      showAddCommentModal: false,
      commentText: null
    };
  },

  computed: {
    ...mapState("meeting", ["currentMeeting", "currentMeetingId", "accountOpenStep"]),
    ...mapGetters("profile", ["canSeeMeetings", "canAddMeetingComment"]),
    ...mapGetters("common", ["loading"]),
    ...mapGetters("meeting", [
      "showCurrentMeeting",
      "accountOpenStepsCount",
      "accountOpenSteps",
      "isAccountOpenFirstStepComplete",
      "isAccountOpenSecondStepComplete",
      "isSurveyStepComplete",
      "showOpenAccountForm",
      "isCurrentStepFlDocs",
      "isCurrentStepMeetingDocs",
      "isCurrentStepSurvey",
      "isCurrentTemplateFields",
      "isTemplateFieldsFilled",
      "isAllFlDocsUploaded",
      "isAllRequiredMeetingDocsUploaded",
      "isSurveyAccopenFormed",
      "wait_sign",
      "isSurveyCanSkip"
    ]),

    waitingForMeetingProcess() {
      return this.currentMeeting?.meeting_state === "process";
    },

    showMeetingDetails: {
      get() {
        return this.showCurrentMeeting;
      },
      set(value) {
        if (!value && !this.loading) {
          this.setCurrentMeeting(null);
          this.setCurrentMeetingId(null);
          this.setAccountOpenStep(0);
        }
      },
    },

    sendDocsDisabled() {
      if (this.isCurrentStepFlDocs) {
        return !this.isAllFlDocsUploaded;
      }

      if (this.isCurrentStepMeetingDocs) {
        if (this.wait_sign === true) return true;

        return !this.isAllRequiredMeetingDocsUploaded;
      }

      if (this.isCurrentTemplateFields) {
        return !this.isTemplateFieldsFilled;
      }

      if (this.isCurrentStepSurvey) {
        return !this.isSurveyAccopenFormed;
      }

      return true;
    },

    isCurrentStepComplete() {
      const currentStep = this.accountOpenSteps[this.accountOpenStep];
      if (currentStep)
      {
        if (currentStep.name === 'send_fls' && this.isAccountOpenFirstStepComplete) {
          return true;
        }
        else if (currentStep.name === 'send_docs' && this.isAccountOpenSecondStepComplete) {
          return true;
        }
        else if (currentStep.name === 'plans') {
          return true;
        }
        else if (currentStep.name === 'survey_accopen' && this.isSurveyStepComplete) {
          return true;
        }
      }

      return false;
    },

    meetingTitle() {
      return this.currentMeeting ? "Встреча №" + this.currentMeeting.num : "";
    },
  },

  watch: {
    showAddCommentModal(v) {
      if (!v) {
        this.commentText = null;
      }
    }
  },

  methods: {
    ...mapMutations("common", ["toggleOptions"]),
    ...mapMutations("meeting", [
      "setCurrentMeeting",
      "setCurrentMeetingId",
      "setAccountOpenStep",
      "setShowOpenAccountForm",
    ]),
    ...mapActions("meeting", ["initMeeting", "getMeetingById", "sendComment"]),

    async meetingFailed() {
      const res = await breakMeeting(this.currentMeetingId);
      if (res) {
        this.showCloseMeetingModal = false;
        this.showMeetingDetails = false;
      }
    },

    openAccountForm() {
      this.initMeeting();
    },
    accountOpenStepBack() {
      this.setAccountOpenStep(this.accountOpenStep - 1);
    },
    accountOpenStepNext() {
      this.setAccountOpenStep(this.accountOpenStep + 1);
    },

    async processMeeting(id) {
      if (this.isCurrentStepFlDocs) {
        return await processMeetingSendFls(id);
      }
      if (this.isCurrentStepMeetingDocs) {
        return await processMeetingSendDocs(id, {
          is_typical_statute: this.$store.state.meeting.isTypicalStatute // FIXME:
        });
      }
      if (this.isCurrentTemplateFields) {
        return await processMeetingTemplateFields(id, this.$store.state.meeting.template_fields_input);
      }
      if (this.isCurrentStepSurvey) {
        return await processMeetingSurvey(id, this.$store.getters['meeting/survey_accopen_data']);
      }
    },

    async sendDocs() {
      if (!this.sendDocsDisabled) {
        const res = await this.processMeeting(this.currentMeetingId);
        if (res) {
          this.getMeetingById(this.currentMeetingId);
        }
      }
    },
  },
};
</script>

<style scoped>
::v-deep .v-banner__text {
  white-space: normal !important;
}
</style>
